import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import type { AdminUser } from "../lib/auth";
import { logActivity } from "../lib/adminData";
import { MENU_SECTIONS, isSectionAllowed } from "../lib/menuSections";

interface NavItem {
  label: string;
  to: string;
  icon: string;
  available: boolean;
}

function NavItemLink({ item, onNavigate }: { item: NavItem; onNavigate?: () => void }) {
  if (!item.available) {
    return (
      <div className="flex items-center gap-3 px-4 py-2.5 rounded-md text-foreground-400 cursor-not-allowed">
        <div className="w-5 h-5 flex items-center justify-center shrink-0">
          <i className={item.icon} />
        </div>
        <span className="flex-1 text-sm font-medium">{item.label}</span>
        <span className="text-[10px] font-semibold uppercase tracking-wide border border-background-300 rounded-full px-2 py-0.5 whitespace-nowrap">
          Em breve
        </span>
      </div>
    );
  }
  return (
    <NavLink
      to={item.to}
      end={item.to === "/admin"}
      onClick={onNavigate}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-2.5 rounded-md text-sm font-medium transition-colors ${
          isActive
            ? "bg-primary-500 text-background-50"
            : "text-foreground-700 hover:bg-background-100 hover:text-primary-600"
        }`
      }
    >
      <div className="w-5 h-5 flex items-center justify-center shrink-0">
        <i className={item.icon} />
      </div>
      <span>{item.label}</span>
    </NavLink>
  );
}

export default function AdminLayout({
  user,
  onLogout,
  children,
}: {
  user: AdminUser;
  onLogout: () => void;
  children: ReactNode;
}) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const lastLoggedPath = useRef("");

  // Registra no histórico do painel cada acesso a uma tela (quem acessou, o
  // que abriu e de qual endereço/IP). Aparece em Logs de atividade.
  useEffect(() => {
    if (location.pathname !== lastLoggedPath.current) {
      lastLoggedPath.current = location.pathname;
      void logActivity("acesso", "painel", location.pathname);
    }
  }, [location.pathname]);
  // Menu lateral respeita as permissões do usuário (admin vê tudo).
  const navItems: NavItem[] = [
    { label: "Dashboard", to: "/admin", icon: "ri-dashboard-line", available: true },
    ...MENU_SECTIONS.filter((s) => isSectionAllowed(user.role, user.permissions, s.key)).map((s) => ({
      label: s.label,
      to: s.to,
      icon: s.icon,
      available: true,
    })),
  ];

  const initial = (user.name || user.email || "A").trim().charAt(0).toUpperCase();
  const roleLabel = user.role === "admin" ? "Administrador" : "Editor";

  const handleLogout = () => {
    void logActivity("logout", "auth", "Saída do painel");
    onLogout();
    navigate("/admin/login", { replace: true });
  };

  const sidebarContent = (
    <>
      <div className="px-5 py-6 border-b border-background-200">
        <Link to="/" className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg bg-primary-500 text-background-50 flex items-center justify-center font-heading font-bold text-lg">
            R
          </div>
          <div className="leading-tight">
            <div className="font-heading font-bold text-foreground-900 text-sm">Real Embalagens</div>
            <div className="text-xs text-foreground-500">Painel administrativo</div>
          </div>
        </Link>
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
        {navItems.map((item) => (
          <NavItemLink key={item.to} item={item} onNavigate={() => setMobileOpen(false)} />
        ))}
      </nav>
      <div className="px-4 py-4 border-t border-background-200">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-accent-500 text-foreground-950 flex items-center justify-center font-semibold text-sm shrink-0">
            {initial}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-foreground-900 truncate">
              {user.name || user.email}
            </div>
            <div className="text-xs text-foreground-500">{roleLabel}</div>
          </div>
          <button
            onClick={handleLogout}
            className="p-2 text-foreground-500 hover:text-accent-700 transition-colors"
            aria-label="Sair"
            title="Sair"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-logout-box-r-line" />
            </div>
          </button>
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-background-100">
      <aside className="hidden lg:flex flex-col w-64 shrink-0 bg-background-50 border-r border-background-200">
        {sidebarContent}
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-foreground-950/40"
            onClick={() => setMobileOpen(false)}
          />
          <aside className="absolute left-0 top-0 bottom-0 w-72 bg-background-50 flex flex-col border-r border-background-200">
            {sidebarContent}
          </aside>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-40 bg-background-50 border-b border-background-200">
          <div className="flex items-center justify-between px-4 md:px-8 h-16">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setMobileOpen(true)}
                className="lg:hidden p-2 text-foreground-700 rounded-md hover:bg-background-100 transition-colors"
                aria-label="Abrir menu"
              >
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-menu-line text-xl" />
                </div>
              </button>
              <div className="lg:hidden font-heading font-bold text-foreground-900">
                Real Embalagens
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Link
                to="/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-foreground-700 rounded-md hover:bg-background-100 hover:text-primary-600 transition-colors whitespace-nowrap"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-external-link-line" />
                </div>
                Ver site
              </Link>
              <button
                onClick={handleLogout}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-foreground-700 rounded-md hover:bg-background-100 hover:text-accent-700 transition-colors whitespace-nowrap"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-logout-box-r-line" />
                </div>
                Sair
              </button>
            </div>
          </div>
        </header>
        <main className="flex-1 p-4 md:p-8">{children}</main>
      </div>
    </div>
  );
}